/**
 * Airport Service — thin wrappers around apiClient's airport endpoints.
 *
 * Two hooks, two different backends:
 *
 * useAirportSearch(query) — TYPEAHEAD path (BE `search=` param). ILIKE
 *   substring match across code, icao, name, city, state, city_code, and
 *   country. Use for dropdowns / autocomplete inputs where the user is
 *   TYPING a partial code or name. Gated at min 2 chars so we don't fire
 *   a request per keystroke of "K" or "T".
 *
 * useAirportByIcao(code) — EXACT LOOKUP path (BE `?icao=` param). Cheap
 *   indexed equality, sub-ms on the server. Use when the FE ALREADY has
 *   the ICAO/FAA code (from a bid.leg_itinerary, a flight record, a
 *   picked-suggestion airport, etc.) and just needs the name/city/coords
 *   for display. Every render surface that reads
 *   `airports.find(a => a.icao === code)` should use this instead.
 *
 * React Query dedupes by key, so N components on the same page asking
 * useAirportByIcao("KTEB") share ONE network call. StaleTime: Infinity
 * because airports rarely change.
 */

import { useQuery, useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiClient } from '@/lib/api';

/**
 * Debounced-friendly typeahead hook. Caller is responsible for debouncing
 * the input state before passing it in — the hook itself just gates on
 * min length + fires the request. `keepPreviousData` avoids flicker while
 * the user types.
 */
export function useAirportSearch(query, { minChars = 2, limit = 30 } = {}) {
  const q = String(query || '').trim();
  return useQuery({
    queryKey: ['airportSearch', q, limit],
    queryFn: () => apiClient.searchAirports(q, { limit }),
    enabled: q.length >= minChars,
    staleTime: 10 * 60 * 1000,
    keepPreviousData: true,
  });
}

/**
 * Exact ICAO/FAA lookup. Returns the airport object or null when the code
 * doesn't resolve.
 */
export function useAirportByIcao(icao) {
  const code = String(icao || '').toUpperCase();
  return useQuery({
    queryKey: ['airportByIcao', code],
    queryFn: () => apiClient.getAirportByIcao(code),
    enabled: !!code,
    staleTime: Infinity,
  });
}

/**
 * Batch enrichment — pass an array of ICAO codes (duplicates OK), get back
 * a Map<uppercase-code, airport | undefined>. Uses React Query's
 * `useQueries` under the hood so callers can look up any number of codes
 * without violating rules of hooks (which forbid calling useAirportByIcao
 * inside a map/loop). Also returns a small { isLoading } flag for a
 * single spinner covering the whole batch.
 */
export function useAirportsByIcao(icaoList) {
  // Content-stable key — callers often pass a FRESH array every render
  // (e.g. flights.map(f => f.airport)), so memoing on the array
  // reference itself would recompute forever. Sorting + joining gives
  // us a string that only changes when the actual set of codes does.
  const codesKey = (icaoList || [])
    .filter(Boolean)
    .map((c) => String(c).toUpperCase())
    .sort()
    .join('|');

  const uniqueCodes = useMemo(
    () => (codesKey ? codesKey.split('|') : []),
    [codesKey],
  );

  const queries = useQueries({
    queries: uniqueCodes.map((code) => ({
      queryKey: ['airportByIcao', code],
      queryFn: () => apiClient.getAirportByIcao(code),
      enabled: !!code,
      staleTime: Infinity,
    })),
  });

  // Fingerprint DATA presence per code — useQueries returns a fresh
  // top-level array every render (its .data entries are stable when the
  // cache is), so depending on `queries` in the Map memo below would
  // rebuild it every render and hand every consumer a new Map identity.
  // That new Map cascades into any downstream useMemo / useEffect / etc.
  // that lists it as a dep — see the render-loop bug this fixed in
  // TripSheetPreviewDrawer.
  const dataKey = queries.map((q) => (q.data ? '1' : '0')).join('') + '|' + codesKey;

  const byIcao = useMemo(() => {
    const m = new Map();
    queries.forEach((q, i) => {
      if (q.data) m.set(uniqueCodes[i], q.data);
    });
    return m;
    // Deliberately depend on the string dataKey rather than the unstable
    // `queries` / `uniqueCodes` references. Same content → same Map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey]);

  const isLoading = queries.some((q) => q.isLoading);
  return { byIcao, isLoading };
}
